import React, { useState } from "react";
import { Button, Form, Alert, Card, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  createCustomer,
  assignSensor,
  setSensorUsage,
  getCustomerSensors,
} from "../services/sensorCrud";
import { haveSame18Prefix } from "./sensorUtils";

export default function CustomerTab({ onSelectSensor }) {
  const { t } = useTranslation();

  const [customerName, setCustomerName] = useState("");
  const [customerPackage, setCustomerPackage] = useState("1");
  const [customerDomain, setCustomerDomain] = useState("");
  const [lastCreatedCustomer, setLastCreatedCustomer] = useState(null);

  // Assign sensor
  const [assignTuiPart, setAssignTuiPart] = useState("");
  const [assignCustomerName, setAssignCustomerName] = useState("");
  const [assignUsage, setAssignUsage] = useState("sold");
  const [assignedCustomerName, setAssignedCustomerName] = useState("");
  const [assignedAt, setAssignedAt] = useState("");
  const [unassignedAt, setUnassignedAt] = useState("");
  const [customerSensors, setCustomerSensors] = useState([]);

  async function assignToAll(tuiPart, cNamePart, usage, assignedAt, unassignedAt) {
    const customerId = !isNaN(cNamePart) && cNamePart !== "" ? Number(cNamePart) : undefined;
    try {
      await assignSensor({ tuiPart, cNamePart, assignedAt, unassignedAt });
      await setSensorUsage({
        tui: tuiPart,
        usage,
        customerId,
      });
    } catch (err) {
      const matches = err?.response?.data?.matching_tuis;
      const prefix = haveSame18Prefix(matches);
      if (matches && prefix) {
        await Promise.all(
          matches.map((tui) =>
            Promise.all([
              assignSensor({ tuiPart: tui, cNamePart, assignedAt, unassignedAt }),
              setSensorUsage({
                tui,
                usage,
                customerId,
              }),
            ])
          )
        );
      } else {
        throw err;
      }
    }
  }

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    try {
      const response = await createCustomer({
        cName: customerName,
        pNumber: parseInt(customerPackage, 10),
        cDomain: customerDomain,
      });
      setLastCreatedCustomer(response);
      setCustomerName("");
      setCustomerPackage("1");
      setCustomerDomain("");
      alert(t("customer_created_successfully"));
    } catch (err) {
      console.error("Failed to create customer:", err);
      alert(t("customer_creation_failed"));
    }
  };

  const handleAssignSensor = async (e) => {
    e.preventDefault();
    try {
      await assignToAll(assignTuiPart, assignCustomerName, assignUsage, assignedAt, unassignedAt);
      const response = await getCustomerSensors({ cNamePart: assignCustomerName });
      setAssignedCustomerName(response?.customer_name || "");
      setCustomerSensors(response?.sensors || []);

      setAssignTuiPart("");
      setAssignedAt("");
      setUnassignedAt("");
      alert(t("sensor_assigned_successfully"));
    } catch (err) {
      console.error("Failed to assign sensor:", err);

      let errorMessage = t("sensor_assignment_failed");
      if (err?.response?.data) {
        if (typeof err.response.data === "string") {
          errorMessage = err.response.data;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data.detail) {
          errorMessage = err.response.data.detail;
        } else {
          errorMessage = JSON.stringify(err.response.data, null, 2);
        }
      }

      alert(`${t("sensor_assignment_failed")}: ${errorMessage}`);
    }
  };

  return (
    <Card>
      <Card.Body>
        <h5>{t("create_customer")}</h5>

        {/* Create Customer */}
        <Form onSubmit={handleCreateCustomer}>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>{t("name")}</Form.Label>
                <Form.Control
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder={t("enter_name")}
                  required
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>{t("package")}</Form.Label>
                <Form.Select
                  value={customerPackage}
                  onChange={(e) => setCustomerPackage(e.target.value)}
                >
                  <option value="1">Cloud Start</option>
                  <option value="2">Cloud Pro</option>
                  <option value="3">Cloud Enterprise</option>
                  <option value="6">Cloud Free</option>
                  <option value="7">Cloud Demo</option>
                  <option value="8">Cloud Start 1k</option>
                  <option value="9">Cloud Pro 1k</option>
                  <option value="10">Cloud Start 5k</option>
                  <option value="11">Cloud Pro 5k</option>
                  <option value="12">Cloud Enterprise 5k</option>
                  <option value="13">Cloud Start 10k</option>
                  <option value="14">Cloud Pro 10k</option>
                  <option value="15">Cloud Enterprise 10k</option>
                  <option value="16">Cloud Start 50k</option>
                  <option value="17">Cloud Pro 50k</option>
                  <option value="18">Cloud Enterprise 50k</option>
                  <option value="19">Cloud Kataster 1k</option>
                  <option value="20">Cloud Kataster 5k</option>
                  <option value="21">Cloud Kataster 10k</option>
                  <option value="22">Cloud Kataster 50k</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>{t("domain")}</Form.Label>
                <Form.Control
                  type="text"
                  value={customerDomain}
                  onChange={(e) => setCustomerDomain(e.target.value)}
                  placeholder={t("enter_domain")}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Button type="submit" variant="primary">
            {t("create_customer")}
          </Button>
          {lastCreatedCustomer && (
            <div className="mt-4 p-3 bg-light rounded border">
              <h6 className="mb-3">{t("last_created_customer")}</h6>
              <Row>
                <Col sm={6} md={4} className="mb-2">
                  <span className="text-muted d-block small fw-bold text-uppercase">
                    Customer ID
                  </span>
                  <span className="fw-semibold">{lastCreatedCustomer.customer_id}</span>
                </Col>
                <Col sm={6} md={4} className="mb-2">
                  <span className="text-muted d-block small fw-bold text-uppercase">Name</span>
                  <span className="fw-semibold">{lastCreatedCustomer.customer_name}</span>
                </Col>
                <Col sm={6} md={4} className="mb-2">
                  <span className="text-muted d-block small fw-bold text-uppercase">Domain</span>
                  <span className="fw-semibold">{lastCreatedCustomer.domain}</span>
                </Col>
                <Col sm={6} md={4} className="mb-2">
                  <span className="text-muted d-block small fw-bold text-uppercase">Package</span>
                  <span className="fw-semibold">{lastCreatedCustomer.package_name}</span>
                </Col>
                <Col sm={6} md={4} className="mb-2">
                  <span className="text-muted d-block small fw-bold text-uppercase">Type</span>
                  <span className="fw-semibold">{lastCreatedCustomer.customer_type}</span>
                </Col>
                {/* <Col sm={6} md={4} className="mb-2">
                  <span className="text-muted d-block small fw-bold text-uppercase">Message</span>
                  <span className="fw-semibold">{lastCreatedCustomer.message}</span>
                </Col> */}
              </Row>
            </div>
          )}
        </Form>

        {/* Assign Sensor */}
        <div className="mt-4">
          <h5>{t("assign_sensor")}</h5>
          <Form onSubmit={handleAssignSensor}>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>{t("tui_part")}</Form.Label>
                  <Form.Control
                    type="text"
                    value={assignTuiPart}
                    onChange={(e) => setAssignTuiPart(e.target.value)}
                    placeholder={t("enter_tui_part_placeholder")}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>{t("customer_name_or_cloud_id")}</Form.Label>
                  <Form.Control
                    type="text"
                    value={assignCustomerName}
                    onChange={(e) => setAssignCustomerName(e.target.value)}
                    placeholder={t("enter_customer_name")}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>{t("usage")}</Form.Label>
                  <Form.Select
                    value={assignUsage}
                    onChange={(e) => setAssignUsage(e.target.value)}
                    required
                  >
                    <option value="sold">{t("sold")}</option>
                    <option value="rented">{t("rented")}</option>
                    <option value="replaced">{t("replaced")}</option>
                    <option value="lost">{t("lost")}</option>
                    <option value="research">{t("research")}</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t("assigned_from_date")}</Form.Label>
                  <Form.Control
                    type="date"
                    value={assignedAt}
                    onChange={(e) => setAssignedAt(e.target.value)}
                    placeholder={t("optional")}
                  />
                  <Form.Text className="text-muted">{t("assigned_from_date_help")}</Form.Text>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t("assigned_until_date")}</Form.Label>
                  <Form.Control
                    type="date"
                    value={unassignedAt}
                    onChange={(e) => setUnassignedAt(e.target.value)}
                    placeholder={t("optional")}
                  />
                  <Form.Text className="text-muted">{t("assigned_until_date_help")}</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Button type="submit" variant="primary">
              {t("assign_sensor")}
            </Button>
          </Form>
          {customerSensors.length > 0 && (
            <Alert variant="info" className="mt-3">
              <strong>{t("customer")}:</strong> {assignedCustomerName || t("unknown_customer")}
              <br />
              <strong>{t("customer_sensors")}:</strong>
              <ul className="mb-0 mt-2">
                {customerSensors.map((sensor, idx) => (
                  <li key={idx}>
                    <Button
                      variant="link"
                      className="p-0 text-decoration-underline"
                      onClick={() => onSelectSensor(sensor.tui)}
                    >
                      {sensor.tui}
                    </Button>
                  </li>
                ))}
              </ul>
            </Alert>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}
